import { refreshTokenAPI } from "api/auth";
import AuthLayout from "components/Auth/Layout";
import { addSeconds, parseISO } from "date-fns";
import AppLayout from "layout/AppLayout";
import ApprovalManagement from "pages/ApprovalManagement";
import Councillor from "pages/Councillors";
import ManageRoles from "pages/ManageRoles";
import NewsManagement from "pages/NewsManagement";
import Notification from "pages/Notification";
import UserManagement from "pages/UserManagement";
import CreatePassword from "pages/auth/CreatePassword";
import OTP from "pages/auth/OTP";
import SignIn from "pages/auth/signIn";
import NotificationApprovalManagement from "pages/NotificationApprovalManagement";
import { useEffect, useRef, useState } from "react";
import {
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  retrievePermissions,
  retrieveToken,
  retrieveUserData,
  storeToken,
} from "./utils/cryptoStorage";

import AllArticles from "pages/AllArticles";
import AllWards from "pages/AllWards";
import ArticleApprovalManagement from "pages/ArticlesApprovalManagement";
import WardsApprovalManagement from "pages/WardsApprovalManagement";
import WardsReport from "pages/WardsReport";

// eslint-disable-next-line react/prop-types
function AuthWrapper({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const debounceTimeoutRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const userData = retrieveUserData();

  useEffect(() => {
    const checkAuth = async () => {
      const token = retrieveToken("accessToken");
      const refreshToken = retrieveToken("refreshToken");
      const tokenExpiryTime = retrieveToken("tokenExpiryTime");

      if (!token) {
        navigate("/login");
        return;
      }

      if (!tokenExpiryTime) {
        navigate("/login");
        return;
      }

      const currentTime = new Date();
      const expiryDate = parseISO(tokenExpiryTime);
      const timeUntilExpiry = expiryDate.getTime() - currentTime.getTime();

      if (timeUntilExpiry <= 2 * 60 * 1000 && refreshToken) {
        try {
          const refreshedToken = await refreshTokenAPI(
            refreshToken,
            userData.user_type
          );
          storeToken("accessToken", refreshedToken?.access_token);
          storeToken("refreshToken", refreshedToken?.refresh_token);

          let newExpiryTime;
          if (refreshedToken?.expires_in) {
            newExpiryTime = addSeconds(
              new Date(),
              refreshedToken.expires_in
            ).toISOString();
            storeToken("tokenExpiryTime", newExpiryTime);
          }
        } catch (error) {
          navigate("/login");
        }
      }
    };

    const debouncedCheckAuth = () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(checkAuth, 1500);
    };

    // Initial check when component mounts
    checkAuth();

    // Listen for user actions to potentially trigger re-authentication
    document.addEventListener("click", debouncedCheckAuth);
    document.addEventListener("mousemove", debouncedCheckAuth);
    document.addEventListener("keydown", debouncedCheckAuth);
    document.addEventListener("scroll", debouncedCheckAuth);
    window.addEventListener("focus", debouncedCheckAuth);

    return () => {
      document.removeEventListener("click", debouncedCheckAuth);
      document.removeEventListener("mousemove", debouncedCheckAuth);
      document.removeEventListener("keydown", debouncedCheckAuth);
      document.removeEventListener("scroll", debouncedCheckAuth);
      window.removeEventListener("focus", debouncedCheckAuth);

      clearTimeout(debounceTimeoutRef.current);
    };
  }, [navigate, location]);
  useEffect(() => {
    // Retrieve the reload status from sessionStorage
    const hasReloaded = localStorage?.getItem("hasReloaded");
    // Check if the page has not been reloaded yet
    if (!hasReloaded) {
      localStorage.setItem("hasReloaded", "true"); // Set the flag in sessionStorage
      window.location.reload();
      setIsInitialLoad(false);
    }
  }, []);

  return isInitialLoad && children;
}

function App() {
  const permissions = retrievePermissions();

  const router = createBrowserRouter([
    // {
    //   path: "/login",
    //   element: <Login />,
    // },
    {
      path: "/login",
      element: (
        <AuthLayout>
          <SignIn />
        </AuthLayout>
      ),
    },
    {
      path: "/forgot",
      element: (
        <AuthLayout>
          <OTP />
        </AuthLayout>
      ),
    },
    {
      path: "/create-password",
      element: (
        <AuthLayout>
          <CreatePassword />
        </AuthLayout>
      ),
    },
    {
      path: "/",
      element: (
        <AuthWrapper>
          <AppLayout>
            <Outlet />
          </AppLayout>
        </AuthWrapper>
      ),
      children: [
        {
          path: "/",
          element: (
            <AuthWrapper path={"/user-management"}>
              <UserManagement />
            </AuthWrapper>
          ),
        },
        {
          path: "/user-management",
          element: (
            <AuthWrapper path={"/user-management"}>
              <UserManagement />
            </AuthWrapper>
          ),
        },
        {
          path: "/user-management/manage-roles",
          element: (
            <AuthWrapper path={"/user-management/manage-roles"}>
              <ManageRoles />
            </AuthWrapper>
          ),
        },
        {
          path: "/notification",
          element: (
            <AuthWrapper path={"/notification"}>
              <Notification />
            </AuthWrapper>
          ),
        },
        {
          path: "/news-management",
          element: (
            <AuthWrapper path={"/news-management"}>
              <NewsManagement />
            </AuthWrapper>
          ),
        },
        {
          path: "/approval-management",
          element: (
            <AuthWrapper path={"/approval-management"}>
              <ApprovalManagement />
            </AuthWrapper>
          ),
        },
        {
          path: "/councillors",
          element: (
            <AuthWrapper path={"/councillors"}>
              <Councillor />
            </AuthWrapper>
          ),
        },
        {
          path: "/all-wards",
          element: (
            <AuthWrapper path={"/all-wards"}>
              <AllWards />
            </AuthWrapper>
          ),
        },
        {
          path: "/wards-approval-management",
          element: (
            <AuthWrapper path={"/wards-approval-management"}>
              <WardsApprovalManagement />
            </AuthWrapper>
          ),
        },
        {
          path: "/projects",
          element: (
            <AuthWrapper path={"/wards-approval-management"}>
              <WardsApprovalManagement />
            </AuthWrapper>
          ),
        },
        {
          path: "/incident-report",
          element: (
            <AuthWrapper path={"/incident-report"}>
              <WardsReport />
            </AuthWrapper>
          ),
        },
        {
          path: "/all-articles",
          element: (
            <AuthWrapper path={"/articles-approval-management"}>
              <AllArticles />
            </AuthWrapper>
          ),
        },
        {
          path: "/articles-approval-management",
          element: (
            <AuthWrapper path={"/articles-approval-management"}>
              <ArticleApprovalManagement />
            </AuthWrapper>
          ),
        },
        {
          path: "/notification-approval-management",
          element: (
            <AuthWrapper>
              <NotificationApprovalManagement />
            </AuthWrapper>
          ),
        },
      ],
    },
  ]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition:Bounce
        style={{ zIndex: 9999 }}
      />
      <RouterProvider router={router} />
    </>
  );
}

export default App;
